import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Music, ShoppingCart, User, Home, Info, HelpCircle, Video, Calendar, 
  Settings, LogIn, LogOut, ChevronDown, Star, Radio, Menu, X, MoreHorizontal,
  GraduationCap, Crown, Palette, BookOpen, Wrench, Play, Library, Users, Award, Mic, Disc, Mail, Headphones, Shirt, Image, Drum
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import AudioPlayer from './AudioPlayer';
import AdminCommandCenter from './admin/AdminCommandCenter';
import DictationButton from './admin/DictationButton';

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart } = useCart();
  const { user, isAuthenticated, logout, isAdmin, isEditor } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // New organized menu structure
  const menuStructure = [
    { 
      label: 'Home', 
      path: '/', 
      icon: Home,
      type: 'link' 
    },
    { 
      label: 'About', 
      path: '/about', 
      icon: Info,
      type: 'link' 
    },
    { 
      label: 'Content', 
      icon: Video,
      type: 'dropdown',
      items: [
        { path: '/music', icon: Music, label: 'Music' },
        { path: '/artwork', icon: Palette, label: 'Artwork' },
        { path: '/sample-store', icon: Disc, label: 'Sample Store' },
        { path: '/registration', icon: Star, label: 'Mardi Gras Brasil 2027' },
      ]
    },
    { 
      label: 'Performances', 
      path: '/performances', 
      icon: Drum,
      type: 'link' 
    },
    { 
      label: 'Learning Center', 
      icon: GraduationCap,
      type: 'dropdown',
      items: [
        { path: '/classes', icon: Calendar, label: 'In-Person Class Schedule' },
        { path: '/live-lessons', icon: Video, label: 'Live Lessons' },
        { path: '/videos', icon: Video, label: 'Instructional Videos' },
        { path: '/courses', icon: GraduationCap, label: 'All Courses' },
        { path: '/certificates', icon: Award, label: 'Certificates' },
        { path: '/capoeira', icon: Users, label: 'Capoeira Angola' },
      ]
    },
    { 
      label: 'Contact Us', 
      icon: Mail,
      type: 'dropdown',
      items: [
        { path: '/booking', icon: Calendar, label: 'Book Us' },
        { path: '/membership-agreement', icon: Users, label: 'Become a Member' },
      ]
    },
    { 
      label: 'The Mestre', 
      icon: User,
      type: 'dropdown',
      items: [
        { path: '/legacy', icon: Star, label: 'Mestre Curtis Legacy' },
        { path: '/mestre-full-story', icon: BookOpen, label: 'Full Story' },
        { path: '/mestre-bullfrog', icon: Star, label: 'Kapoeira Do Sapo' },
        { path: '/pandeiro-juggling', icon: Star, label: 'Pandeiro Academy' },
        { path: '/kapoeira-book', icon: BookOpen, label: 'Kapoeira Book' },
        { path: '/play-along', icon: Video, label: 'Play Along with the Master' },
        { path: '/drum-repair', icon: Wrench, label: 'Drum Repair' },
        { path: '/recording-studio', icon: Mic, label: 'Recording Studio' },
        { path: '/live-studio', icon: Video, label: 'Live Studio' },
        { path: '/batucada', icon: Drum, label: 'Brasilian Grooves' },
        { path: '/mobile-studio', icon: Mic, label: 'Mobile Studio' },
      ]
    },
    { 
      label: 'More', 
      icon: MoreHorizontal,
      type: 'dropdown',
      items: [
        { path: '/live', icon: Radio, label: 'Live Stream' },
        { path: '/costume-shop', icon: Shirt, label: 'Costume Shop' },
        { path: '/gallery', icon: Image, label: 'Photo Gallery' },
        { path: '/podcasts', icon: Headphones, label: 'Podcasts' },
        { 
          label: 'Community', 
          icon: Users, 
          type: 'submenu', 
          items: [
            { path: '/studio-gallery', icon: Music, label: 'Community Recordings' },
            { path: '/my-lessons', icon: GraduationCap, label: 'My Lessons' },
            { path: '/library', icon: Library, label: 'My Library' },
          ]
        },
        { path: '/help', icon: HelpCircle, label: 'Help' },
      ]
    },
  ];

  // All links for mobile menu (flattened)
  const allLinks = menuStructure.flatMap(item => 
    item.type === 'dropdown' ? item.items : [{ path: item.path, icon: item.icon, label: item.label }]
  );

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  const toggleDropdown = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
    setActiveSubmenu(null);
  };

  return (
    <div className="min-h-screen bg-background noise-texture">
      {/* Navbar */}
      <nav className="fixed z-10 top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5 h-20 flex items-center px-4 md:px-8" data-testid="main-navbar">
        <div className="max-w-screen-2xl mx-auto w-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="logo-link">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden shadow-glow">
              <img loading="lazy" decoding="async" src="/casa-samba-logo.png" alt="Escola de Samba Casa Samba" className="w-full h-full object-cover" />
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {menuStructure.map((item) => {
              const Icon = item.icon;
              
              if (item.type === 'link') {
                const isActive = isActivePath(item.path);
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-lg whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              }
              
              // Dropdown menu
              const isDropdownActive = item.items.some(l => isActivePath(l.path));
              return (
                <div key={item.label} className="relative">
                  <button
                    onClick={() => toggleDropdown(item.label)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-lg whitespace-nowrap transition-all ${
                      isDropdownActive
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-btn`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>

                  {activeDropdown === item.label && (
                    <div className="absolute top-full left-0 mt-2 w-52 bg-card border border-border/50 rounded-xl shadow-float overflow-visible z-50">
                      {item.items.map((link) => {
                        const LinkIcon = link.icon;
                        // Nested submenu (e.g. Community inside More)
                        if (link.type === 'submenu') {
                          const submenuKey = `${item.label}::${link.label}`;
                          const submenuOpen = activeSubmenu === submenuKey;
                          return (
                            <div key={link.label} className="relative">
                              <button
                                onClick={(e) => { e.stopPropagation(); setActiveSubmenu(submenuOpen ? null : submenuKey); }}
                                className="flex items-center justify-between w-full px-4 py-3 text-base text-foreground hover:bg-white/5 transition-colors"
                                data-testid={`nav-${link.label.toLowerCase()}-submenu-btn`}
                              >
                                <span className="flex items-center gap-3">
                                  <LinkIcon className="w-4 h-4" />
                                  {link.label}
                                </span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${submenuOpen ? '-rotate-90' : '-rotate-90'}`} />
                              </button>
                              {submenuOpen && (
                                <div className="absolute top-0 left-full ml-1 w-56 bg-card border border-border/50 rounded-xl shadow-float overflow-hidden z-50">
                                  {link.items.map((sub) => {
                                    const SubIcon = sub.icon;
                                    const subActive = isActivePath(sub.path);
                                    return (
                                      <Link
                                        key={sub.path}
                                        to={sub.path}
                                        onClick={() => { setActiveDropdown(null); setActiveSubmenu(null); }}
                                        className={`flex items-center gap-3 px-4 py-3 text-base transition-colors ${
                                          subActive
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-foreground hover:bg-white/5'
                                        }`}
                                      >
                                        <SubIcon className="w-4 h-4" />
                                        <span>{sub.label}</span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        const isActive = isActivePath(link.path);
                        return (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => { setActiveDropdown(null); setActiveSubmenu(null); }}
                            className={`flex items-center gap-3 px-4 py-3 text-base transition-colors ${
                              isActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-foreground hover:bg-white/5'
                            }`}
                          >
                            <LinkIcon className="w-4 h-4" />
                            <span>{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Section: Cart + Auth + Mobile Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Cart Button */}
            <Link
              to="/cart"
              className="relative flex items-center gap-2 px-3 md:px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full font-bold transition-all hover:scale-105 shadow-glow"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden md:inline">Cart</span>
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-accent text-white text-xs rounded-full flex items-center justify-center font-bold" data-testid="cart-count">
                  {cart.length}
                </span>
              )}
            </Link>

            {/* Auth Section - Desktop */}
            <div className="hidden md:block">
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-2 bg-card border border-border/50 rounded-full hover:bg-white/5 transition-all"
                    data-testid="user-menu-button"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-black font-bold text-sm">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-card border border-border/50 rounded-xl shadow-float overflow-hidden z-50" data-testid="user-dropdown">
                      <div className="px-4 py-3 border-b border-border/30">
                        <p className="font-medium text-foreground">{user?.first_name} {user?.last_name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                          user?.role === 'admin' || user?.role === 'super_admin' ? 'bg-red-500/20 text-red-400' : 
                          user?.role === 'editor' ? 'bg-blue-500/20 text-blue-400' : 
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {user?.role?.toUpperCase()}
                        </span>
                      </div>
                      
                      {(isAdmin() || isEditor()) && (
                        <Link
                          to="/admin/dashboard"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-foreground"
                          data-testid="admin-dashboard-link"
                        >
                          <Settings className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      )}
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-red-400 w-full text-left"
                        data-testid="logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-4 py-2 border border-border/50 rounded-full text-foreground hover:bg-white/5 transition-all"
                  data-testid="login-button"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-sm font-medium">Login</span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div className={`fixed top-20 right-0 bottom-0 w-72 bg-card border-l border-border/50 z-40 transform transition-transform lg:hidden ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-4 space-y-2 overflow-y-auto h-full pb-32">
          {allLinks.map((link) => {
            const Icon = link.icon;
            const isActive = isActivePath(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? 'bg-primary/20 text-primary'
                    : 'text-foreground hover:bg-white/5'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{link.label}</span>
              </Link>
            );
          })}

          {/* Divider */}
          <div className="border-t border-border/30 my-4" />

          {/* Auth in mobile menu */}
          {isAuthenticated ? (
            <>
              <div className="px-4 py-3">
                <p className="font-medium text-foreground">{user?.first_name} {user?.last_name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              {(isAdmin() || isEditor()) && (
                <Link
                  to="/admin/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-white/5"
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Admin Dashboard</span>
                </Link>
              )}
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-white/5 w-full text-left"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </>
          ) : (
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-white/5"
            >
              <LogIn className="w-5 h-5" />
              <span className="font-medium">Login</span>
            </Link>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-20 pb-32">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-stone-900/50 border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Escola de Samba Casa Samba. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-amber-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-amber-400 transition-colors">
                Terms of Service
              </Link>
              <Link to="/help" className="text-gray-400 hover:text-amber-400 transition-colors">
                Help
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Audio Player */}
      <AudioPlayer />

      {/* Global Admin Command Center — floating crown button, admin-only, all pages */}
      <AdminCommandCenter />

      {/* Global Dictation (speech-to-text) button — admin-only, all pages */}
      <DictationButton />

      {/* Click outside to close dropdowns */}
      {(showUserMenu || activeDropdown) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setShowUserMenu(false); setActiveDropdown(null); }}
        />
      )}
    </div>
  );
};
